import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { primaryTabs, getActivePrimaryTab } from '@/constants/primary-navigation';
import type { PrimaryTab } from '@/constants/primary-navigation';

interface IconRailProps {
  companionOpen: boolean;
  onCompanionToggle: () => void;
  nudgeBadgeCount?: number;
}

const RailItem: React.FC<{
  tab: PrimaryTab;
  isActive: boolean;
  onClick: () => void;
  badgeCount?: number;
}> = ({ tab, isActive, onClick, badgeCount }) => {
  const Icon = tab.icon;

  return (
    <button
      onClick={onClick}
      className={cn(
        'relative flex flex-col items-center justify-center w-full py-3 px-1 gap-1',
        'transition-all duration-200 group',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset',
        isActive
          ? 'text-primary-600 dark:text-primary-400'
          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200',
      )}
      aria-label={tab.label}
      aria-current={isActive ? 'page' : undefined}
    >
      {/* Active indicator bar */}
      <div
        className={cn(
          'absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-8 rounded-r-full transition-all duration-200',
          isActive
            ? 'bg-primary-600 dark:bg-primary-400 opacity-100'
            : 'opacity-0 group-hover:opacity-50 group-hover:bg-gray-400',
        )}
      />

      <div className="relative">
        <Icon
          className={cn(
            'h-5 w-5 transition-transform duration-200',
            isActive && 'scale-110',
            tab.isCompanion && isActive && 'animate-sparkle',
          )}
        />
        {/* Badge for nudge count */}
        {badgeCount != null && badgeCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
            {badgeCount > 9 ? '9+' : badgeCount}
          </span>
        )}
      </div>

      <span
        className={cn(
          'text-[10px] font-medium leading-tight transition-colors duration-200',
          isActive ? 'text-primary-600 dark:text-primary-400' : '',
        )}
      >
        {tab.label}
      </span>
    </button>
  );
};

const IconRail: React.FC<IconRailProps> = ({
  companionOpen,
  onCompanionToggle,
  nudgeBadgeCount,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = getActivePrimaryTab(location.pathname);

  const handleTabClick = (tab: PrimaryTab) => {
    if (tab.isCompanion) {
      onCompanionToggle();
    } else if (tab.path) {
      navigate(tab.path);
    }
  };

  return (
    <aside
      className="hidden lg:flex flex-col items-center w-[72px] flex-shrink-0 bg-white dark:bg-gray-950 border-r border-gray-200 dark:border-gray-800 z-20"
      aria-label="Primary navigation"
    >
      <nav className="flex flex-col items-center w-full pt-4 gap-1 flex-1">
        {primaryTabs.map((tab) => (
          <RailItem
            key={tab.id}
            tab={tab}
            isActive={tab.isCompanion ? companionOpen : activeTab === tab.id}
            onClick={() => handleTabClick(tab)}
            badgeCount={tab.isCompanion ? nudgeBadgeCount : undefined}
          />
        ))}
      </nav>
    </aside>
  );
};

export default IconRail;
